import { Button, ButtonGroup, H2 } from "@datapay-by-datacom/design-system";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { useNavigate } from "react-router";
import { UnsavedProgressConfirmModal } from "../../onboarding/components/UnsavedProgressConfirmModal";
import { useUnsavedProgressNavigationGuard } from "../../onboarding/hooks/useUnsavedProgressNavigationGuard";
import { WorkflowActionBar } from "../../shared/components";
import { Pagination } from "../components/Pagination/Pagination";
import {
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  PAY_COMPONENT_IMPORT_BASE_PATH,
  clampPageSize,
} from "../config";
import { useCompanyMappings } from "../hooks/useCompanyMappings";
import { useComponentsParameters } from "../hooks/useComponentsParameters";
import { useCompanyProvisionAgentId } from "../../onboarding/hooks/useCompanyProvisionAgentId";
import { FeatureFlag, useFeatureFlagValue } from "../../feature-flags";
import { usePagination } from "../hooks/usePagination";
import { useWizard } from "../hooks/useWizard";
import {
  CancelImportConfirmModal,
  CommitImportConfirmModal,
  PartialImportConfirmModal,
  PayComponentRowEditModal,
} from "../redesign";
import {
  addEditedRowIndex,
  clusterPayComponentRows,
  countPayComponentTabs,
  filterPayComponentGroupsBySearch,
  firstPayComponentClusterIdForTab,
  formatPayComponentBlockedBanner,
  getVisiblePayComponentGroups,
  groupPayComponentRowsByClusters,
  type PayComponentClusterId,
  type PayComponentTabId,
} from "../redesign/payComponentImportClustering";
import {
  applyPayComponentRowDraftToPreview,
  serializePreviewToCsvText,
  type PayComponentRowDraft,
} from "../redesign/payComponentRowEditorDraft";
import { partitionCsvByRows } from "../utils/commitChunks";
import { buildComponentResolutionMap } from "../utils/componentAmbiguity";
import { distinctComponentNames } from "../utils/componentNames";
import { buildMappedRows, type MappedRow } from "../utils/mappedRows";
import { buildFailedRowsCsv, downloadCsvText } from "../utils/resultsExport";
import { csvTextToFile } from "../utils/csvTextToFile";
import s from "./ReviewScreen.module.css";

const REDESIGN_ALLOWED_NAV_PATHS = [
  PAY_COMPONENT_IMPORT_BASE_PATH,
  `${PAY_COMPONENT_IMPORT_BASE_PATH}/map`,
  `${PAY_COMPONENT_IMPORT_BASE_PATH}/review`,
  `${PAY_COMPONENT_IMPORT_BASE_PATH}/errors`,
  `${PAY_COMPONENT_IMPORT_BASE_PATH}/results`,
];

const REDESIGN_TABS: PayComponentTabId[] = [
  "all",
  "errors",
  "edited",
  "passed",
];

const TAB_LABELS: Record<PayComponentTabId, string> = {
  all: "All",
  errors: "Errors",
  edited: "Edited",
  passed: "Passed",
};

export interface ReviewScreenProps {
  redesignEnabled?: boolean;
}

/**
 * Review screen — shown after a dry-run (status "valid" or "invalid").
 */
export function ReviewScreen({ redesignEnabled = false }: ReviewScreenProps) {
  const {
    companyCode,
    companyName,
    state,
    updateState,
    commitImport,
    invalidateValidation,
    discardUnsavedProgress,
    runImport,
    isCommitting,
    isValidating,
  } = useWizard();
  const navigate = useNavigate();
  const agentConfig = useCompanyProvisionAgentId();
  // Step 13 (LADR-039 / #440): Track 2 (workloads) commits via Slice 5 REST
  // APIs and needs no onboarding agent id — only Track 1's `invokeAgent`
  // commit does. Bypass the agent-config gate below when the cutover flag is
  // on, rather than blocking Confirm/Import on config Track 2 never reads.
  const useWorkloadsCutover = useFeatureFlagValue(FeatureFlag.EnableWorkloadsCutover);

  const preview = state.preview;
  const importResult = state.importResult;
  const [isRevalidating, setIsRevalidating] = useState(false);
  const hasUncommittedProgress = !!preview && preview.rows.length > 0;
  const { isBlocked, stay, leaveAnyway } = useUnsavedProgressNavigationGuard(
    redesignEnabled && hasUncommittedProgress && !isCommitting,
    REDESIGN_ALLOWED_NAV_PATHS,
  );
  const handleLeaveAnyway = () => {
    discardUnsavedProgress();
    leaveAnyway();
  };
  const rows = useMemo(() => preview?.rows ?? [], [preview]);

  const componentColumn = state.componentColumn ?? preview?.componentColumn ?? "";

  const componentNames = useMemo(
    () => distinctComponentNames(rows, componentColumn),
    [rows, componentColumn],
  );

  const paramsQuery = useComponentsParameters(
    companyCode,
    componentNames,
    state.mypayEnvironment,
  );
  const mappingsQuery = useCompanyMappings(companyCode);

  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [showFailedOnly, setShowFailedOnly] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [commitConfirmOpen, setCommitConfirmOpen] = useState(false);
  const [partialConfirmOpen, setPartialConfirmOpen] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [editingRowIndex, setEditingRowIndex] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<PayComponentTabId>("all");
  const [openClusters, setOpenClusters] = useState<
    ReadonlySet<PayComponentClusterId>
  >(() => new Set());
  const clustersSeededRef = useRef(false);

  const reviewRows = useMemo(
    () =>
      buildMappedRows({
        preview,
        componentColumn,
        startDateColumn: state.startDateColumn,
        endDateColumn: state.endDateColumn,
        paramsByComponent: paramsQuery.data ?? {},
        mappings: mappingsQuery.data ?? [],
      }),
    [
      preview,
      componentColumn,
      state.startDateColumn,
      state.endDateColumn,
      paramsQuery.data,
      mappingsQuery.data,
    ],
  );

  const errorsByRow = useMemo(() => {
    const map = new Map<number, string[]>();
    for (const e of importResult?.errors ?? []) {
      const list = map.get(e.row) ?? [];
      list.push(e.error);
      map.set(e.row, list);
    }
    return map;
  }, [importResult]);

  const errorSummary = useMemo(() => {
    const counts = new Map<string, number>();
    for (const e of importResult?.errors ?? []) {
      const key = e.error.replace(/'[^']*'/g, "'…'");
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [importResult]);

  const hasErrors = errorsByRow.size > 0;
  const failedRows = useMemo(
    () => reviewRows.filter((r) => errorsByRow.has(r.index)),
    [reviewRows, errorsByRow],
  );
  const validCount = reviewRows.length - errorsByRow.size;
  const effectiveFailedOnly = hasErrors && showFailedOnly;

  const clusteredRows = useMemo(
    () =>
      clusterPayComponentRows({
        reviewRows,
        errorsByRow,
        editedRowIndexes: state.editedRowIndexes,
      }),
    [reviewRows, errorsByRow, state.editedRowIndexes],
  );
  const allGroups = useMemo(
    () => groupPayComponentRowsByClusters(clusteredRows),
    [clusteredRows],
  );
  const tabCounts = useMemo(
    () => countPayComponentTabs(clusteredRows),
    [clusteredRows],
  );
  const searchedGroups = useMemo(
    () => filterPayComponentGroupsBySearch(allGroups, searchQuery),
    [allGroups, searchQuery],
  );
  const visibleGroups = useMemo(
    () => getVisiblePayComponentGroups(activeTab, searchedGroups),
    [activeTab, searchedGroups],
  );

  // Open the first visible cluster once data is ready (redesign only).
  useEffect(() => {
    if (!redesignEnabled || clustersSeededRef.current || allGroups.length === 0) {
      return;
    }
    clustersSeededRef.current = true;
    const firstId = firstPayComponentClusterIdForTab("all", allGroups);
    if (firstId) {
      setOpenClusters(new Set([firstId]));
    }
  }, [redesignEnabled, allGroups]);

  // After re-validate clears edited rows, leave the Edited tab.
  useEffect(() => {
    if (activeTab === "edited" && tabCounts.edited === 0) {
      setActiveTab("all");
    }
  }, [activeTab, tabCounts.edited]);

  const trimmedSearch = searchQuery.trim().toLowerCase();
  const searchedRows = useMemo(() => {
    const base = effectiveFailedOnly ? failedRows : reviewRows;
    if (!redesignEnabled || trimmedSearch === "") return base;
    return base.filter((row) => {
      const haystack = [
        row.employeeCode,
        row.employeeName,
        row.component,
        row.startDate ?? "",
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(trimmedSearch);
    });
  }, [
    effectiveFailedOnly,
    failedRows,
    reviewRows,
    redesignEnabled,
    trimmedSearch,
  ]);

  const pageSize = state.pageSize ?? DEFAULT_PAGE_SIZE;
  const { page, setPage, pageCount, pageItems, total } = usePagination(
    searchedRows,
    pageSize,
  );
  const showPager = !redesignEnabled && total > PAGE_SIZE_OPTIONS[0];

  const isLoading = paramsQuery.isLoading || mappingsQuery.isLoading;
  const commitDataReady =
    !paramsQuery.isLoading &&
    !mappingsQuery.isLoading &&
    paramsQuery.data !== undefined &&
    mappingsQuery.data !== undefined;

  const validationStale = state.validationStale;
  const staleReason =
    validationStale && redesignEnabled
      ? "File or mappings changed since validation. Re-validate before importing."
      : null;
  const showRevalidate = redesignEnabled && validationStale;
  const reviewBusy = isCommitting || isValidating || isRevalidating;

  const commitReady = useWorkloadsCutover || agentConfig.isConfigured;
  // Gate on isCommitting too so a fast double-click / back-nav can't start two
  // concurrent commit invocations (risking duplicate payroll writes).
  const canConfirm =
    commitDataReady &&
    importResult?.status === "valid" &&
    commitReady &&
    state.mypayEnvironment !== null &&
    !isCommitting &&
    !validationStale;
  const commitUnavailable =
    !useWorkloadsCutover && !agentConfig.isLoading && !agentConfig.isConfigured;

  const confirmedMappings = useMemo(
    () =>
      (mappingsQuery.data ?? []).map((m) => ({
        elementQualifiedName: m.elementQualifiedName,
        parameterQualifiedName: m.parameterQualifiedName,
        parameterDisplayName: m.parameterDisplayName,
        columnName: m.columnName,
      })),
    [mappingsQuery.data],
  );

  const componentResolutions = useMemo(
    () =>
      buildComponentResolutionMap(
        paramsQuery.data ?? {},
        state.componentResolutions ?? {},
      ),
    [paramsQuery.data, state.componentResolutions],
  );

  const runCommit = (csvText: string, failedRowsCsv: string | null) => {
    if (!state.mypayEnvironment) return;
    void commitImport({
      csvText,
      providerId: state.providerId,
      componentColumn: state.componentColumn,
      startDateColumn: state.startDateColumn,
      endDateColumn: state.endDateColumn,
      mypayEnvironment: state.mypayEnvironment,
      failedRowsCsv,
      mappings: confirmedMappings,
      componentResolutions,
    });
  };

  const handleConfirm = async () => {
    setConfirmError(null);
    if (isCommitting || validationStale) return;
    if (!state.csvText) {
      setConfirmError("No CSV to import. Please re-upload the file.");
      return;
    }
    if (!state.mypayEnvironment) {
      setConfirmError("Select a Datapay environment in the company gate.");
      return;
    }
    runCommit(state.csvText, null);
  };

  const handleImportValid = async () => {
    setConfirmError(null);
    if (isCommitting || validationStale) return;
    if (!state.csvText || !importResult) {
      setConfirmError("No CSV to import. Please re-upload the file.");
      return;
    }
    if (!state.mypayEnvironment) {
      setConfirmError("Select a Datapay environment in the company gate.");
      return;
    }
    const { validCsv } = partitionCsvByRows(state.csvText, (rn) =>
      errorsByRow.has(rn),
    );
    const failedRowsCsv = buildFailedRowsCsv(state.csvText, importResult.errors);
    runCommit(validCsv, failedRowsCsv);
  };

  const handleRequestConfirm = () => {
    if (redesignEnabled) {
      setCommitConfirmOpen(true);
      return;
    }
    void handleConfirm();
  };

  const handleRequestPartialImport = () => {
    if (redesignEnabled) {
      setPartialConfirmOpen(true);
      return;
    }
    void handleImportValid();
  };

  const handleRowSave = (draft: PayComponentRowDraft) => {
    if (!preview || editingRowIndex === null) return;
    const rowIndex = editingRowIndex;
    const nextPreview = applyPayComponentRowDraftToPreview(
      preview,
      rowIndex,
      draft,
      componentColumn,
      {
        startDateColumn: state.startDateColumn,
        endDateColumn: state.endDateColumn,
      },
    );
    const csvText = serializePreviewToCsvText(nextPreview);
    updateState({
      preview: nextPreview,
      csvText,
      file: csvTextToFile(csvText, state.file?.name ?? "import.csv"),
      editedRowIndexes: addEditedRowIndex(state.editedRowIndexes, rowIndex),
    });
    invalidateValidation();
    setEditingRowIndex(null);
    // Keep All (or current tab) so the user can keep editing other rows;
    // open the Edited cluster so it is visible without switching tabs.
    setOpenClusters((prev) => new Set([...prev, "edited"]));
  };

  const handleRequestCancelImport = () => {
    if (reviewBusy) return;
    setCancelConfirmOpen(true);
  };

  const handleConfirmCancelImport = () => {
    setCancelConfirmOpen(false);
    discardUnsavedProgress();
    void navigate(PAY_COMPONENT_IMPORT_BASE_PATH);
  };

  const handleRevalidate = async () => {
    if (reviewBusy || !validationStale) return;
    setConfirmError(null);
    setIsRevalidating(true);
    try {
      await runImport(true);
    } catch (err) {
      setConfirmError(
        err instanceof Error ? err.message : "Re-validation failed.",
      );
    } finally {
      setIsRevalidating(false);
    }
  };

  const handleTabChange = (tab: PayComponentTabId) => {
    setActiveTab(tab);
    const firstId = firstPayComponentClusterIdForTab(tab, searchedGroups);
    setOpenClusters(firstId ? new Set([firstId]) : new Set());
  };

  const visibleReviewTabs = useMemo(
    () =>
      REDESIGN_TABS.filter(
        (tab) => tab !== "edited" || tabCounts.edited > 0,
      ),
    [tabCounts.edited],
  );

  const handleReviewTabKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const currentIndex = visibleReviewTabs.indexOf(activeTab);
    if (currentIndex < 0) return;
    let nextIndex = currentIndex;
    switch (event.key) {
      case "ArrowRight":
        nextIndex = (currentIndex + 1) % visibleReviewTabs.length;
        break;
      case "ArrowLeft":
        nextIndex =
          (currentIndex - 1 + visibleReviewTabs.length) %
          visibleReviewTabs.length;
        break;
      case "Home":
        nextIndex = 0;
        break;
      case "End":
        nextIndex = visibleReviewTabs.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    handleTabChange(visibleReviewTabs[nextIndex]);
  };

  const toggleCluster = (clusterId: PayComponentClusterId) => {
    setOpenClusters((prev) => {
      const next = new Set(prev);
      if (next.has(clusterId)) next.delete(clusterId);
      else next.add(clusterId);
      return next;
    });
  };

  const editingMappedRow =
    editingRowIndex !== null
      ? reviewRows.find((row) => row.index === editingRowIndex) ?? null
      : null;
  const editingSourceRow =
    editingRowIndex !== null && preview
      ? preview.rows[editingRowIndex - 1] ?? null
      : null;

  const backTarget = redesignEnabled
    ? PAY_COMPONENT_IMPORT_BASE_PATH
    : `${PAY_COMPONENT_IMPORT_BASE_PATH}/map`;

  const renderRowTable = (mappedRows: readonly MappedRow[]) => (
    <div className={s.tableWrapper} role="region" aria-label="Import review" tabIndex={0}>
      <table className={s.reviewTable}>
        <thead>
          <tr>
            {redesignEnabled && (
              <th scope="col" className={s.actionsColumn} aria-label="Actions">
                {/* Intentionally empty — keeps the sticky Edit column narrow */}
              </th>
            )}
            <th scope="col">Row #</th>
            <th scope="col">Employee code</th>
            <th scope="col">Employee name</th>
            <th scope="col">Component</th>
            <th scope="col">Start date</th>
            <th scope="col">End date</th>
            <th scope="col">CSV column</th>
            <th scope="col">Parameter</th>
            <th scope="col">Value</th>
            <th scope="col">Checks</th>
          </tr>
        </thead>
        <tbody>
          {mappedRows.map((r) => {
            const rowErrors = errorsByRow.get(r.index) ?? [];
            const passed = rowErrors.length === 0;
            const checksCell = (rowSpan: number) => (
              <td className={s.checkCell} rowSpan={rowSpan}>
                <div className={s.checkInner}>
                  <span
                    className={passed ? s.badgePass : s.badgeFail}
                    aria-label={passed ? "Passed" : "Failed"}
                  >
                    {passed ? "Pass" : "Fail"}
                  </span>
                  {!passed && (
                    <ul className={s.reasons}>
                      {rowErrors.map((msg, i) => (
                        <li key={i} className={s.reason}>{msg}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </td>
            );
            const actionsCell = (rowSpan: number) =>
              redesignEnabled ? (
                <td className={s.actionsCell} rowSpan={rowSpan}>
                  <Button
                    variant="tertiary"
                    onPress={() => setEditingRowIndex(r.index)}
                    isDisabled={reviewBusy}
                    aria-label={`Edit row ${r.index}${r.employeeCode ? ` (${r.employeeCode})` : ""}`}
                  >
                    Edit
                  </Button>
                </td>
              ) : null;

            if (r.values.length === 0) {
              return (
                <tr
                  key={r.index}
                  id={`review-row-${r.index}`}
                  className={passed ? undefined : s.failRow}
                >
                  {actionsCell(1)}
                  <td className={s.num}>{r.index}</td>
                  <td>{r.employeeCode}</td>
                  <td>{r.employeeName}</td>
                  <td>{r.component}</td>
                  <td>{r.startDate ?? "—"}</td>
                  <td>{r.endDate ?? "—"}</td>
                  <td className={s.dim} colSpan={3}>
                    Assignment only (no mapped values)
                  </td>
                  {checksCell(1)}
                </tr>
              );
            }
            return r.values.map((v, j) => (
              <tr
                key={`${r.index}-${v.column}`}
                id={j === 0 ? `review-row-${r.index}` : undefined}
                className={passed ? undefined : s.failRow}
              >
                {j === 0 && actionsCell(r.values.length)}
                {j === 0 && (
                  <>
                    <td className={s.num} rowSpan={r.values.length}>
                      {r.index}
                    </td>
                    <td rowSpan={r.values.length}>{r.employeeCode}</td>
                    <td rowSpan={r.values.length}>{r.employeeName}</td>
                    <td rowSpan={r.values.length}>{r.component}</td>
                    <td rowSpan={r.values.length}>{r.startDate ?? "—"}</td>
                    <td rowSpan={r.values.length}>{r.endDate ?? "—"}</td>
                  </>
                )}
                <td><code>{v.column}</code></td>
                <td>
                  {v.parameter}
                  {v.sealed && <span className={s.sealed}> (sealed)</span>}
                </td>
                <td className={s.val}>{v.value || "—"}</td>
                {j === 0 && checksCell(r.values.length)}
              </tr>
            ));
          })}
        </tbody>
      </table>
    </div>
  );

  if (!preview || !importResult) {
    return (
      <div className={s.container}>
        <header className={s.header}>
          <div className={s.headerText}>
            <H2 className={s.title}>Review import</H2>
            <p className={s.subtitle}>No import to review. Please upload a CSV first.</p>
          </div>
        </header>
        <ButtonGroup align="right">
          <Button variant="tertiary" onPress={() => navigate(PAY_COMPONENT_IMPORT_BASE_PATH)}>
            Back to upload
          </Button>
        </ButtonGroup>
      </div>
    );
  }

  const actionBarStatus = staleReason ?? (confirmError ? confirmError : undefined);

  return (
    <div className={s.container}>
      <header className={s.header}>
        <div className={s.headerText}>
          <H2 className={s.title}>Review import</H2>
          <p className={s.subtitle}>
            Review each row and the parameter every mapped column will write to
            before importing. The <strong>Checks</strong> column shows whether
            each row passed pre-flight validation, and why it failed. Unmapped
            columns are not shown.
          </p>
        </div>
      </header>

      {hasErrors && (
        <div className={s.warnBanner} role="alert">
          <strong>
            {redesignEnabled
              ? formatPayComponentBlockedBanner(errorsByRow.size, reviewRows.length)
              : `${errorsByRow.size} of ${reviewRows.length} row(s) failed validation.`}
          </strong>
          {!redesignEnabled && (
            <>
              {" "}
              Fix the CSV and re-upload, or import the {validCount} valid row(s) now
              and re-upload the downloaded failures once corrected.
            </>
          )}
          {!redesignEnabled && errorSummary.length > 0 && (
            <ul className={s.errorSummary}>
              {errorSummary.map(([reason, count]) => (
                <li key={reason}>
                  <strong>{count}×</strong> {reason}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {redesignEnabled ? (
        <div
          className={s.tabs}
          role="tablist"
          aria-label="Review row filter"
          onKeyDown={handleReviewTabKeyDown}
        >
          {visibleReviewTabs.map((tab) => {
            const count =
              tab === "all"
                ? tabCounts.all
                : tab === "errors"
                  ? tabCounts.errors
                  : tab === "edited"
                    ? tabCounts.edited
                    : tabCounts.passed;
            const selected = activeTab === tab;
            return (
              <button
                key={tab}
                type="button"
                role="tab"
                id={`pci-review-tab-${tab}`}
                aria-selected={selected}
                aria-controls={`pci-review-tabpanel-${tab}`}
                tabIndex={selected ? 0 : -1}
                className={`${s.tab} ${selected ? s.tabActive : ""}`}
                onClick={() => handleTabChange(tab)}
              >
                {TAB_LABELS[tab]} ({count})
              </button>
            );
          })}
        </div>
      ) : (
        hasErrors && (
          <div className={s.filterBar} role="group" aria-label="Row filter">
            <button
              type="button"
              className={`${s.filterButton} ${effectiveFailedOnly ? s.filterActive : ""}`}
              aria-pressed={effectiveFailedOnly}
              onClick={() => setShowFailedOnly(true)}
            >
              Failed only ({errorsByRow.size})
            </button>
            <button
              type="button"
              className={`${s.filterButton} ${!effectiveFailedOnly ? s.filterActive : ""}`}
              aria-pressed={!effectiveFailedOnly}
              onClick={() => setShowFailedOnly(false)}
            >
              All rows ({reviewRows.length})
            </button>
          </div>
        )
      )}

      {redesignEnabled && (
        <input
          type="search"
          className={s.nameSearch}
          placeholder="Search employee or component…"
          aria-label="Search review rows"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      )}

      {importResult.status === "valid" && (
        <p className={s.okBanner} role={redesignEnabled ? "status" : undefined}>
          Pre-flight passed — nothing has been written yet. Confirm to import{" "}
          {reviewRows.length} row(s).
        </p>
      )}

      {commitUnavailable && (
        <div className={s.warnBanner} role="alert">
          <strong>Import is not available.</strong> The onboarding agent is not
          configured for this environment (ONBOARDING_AGENT_ID /
          ONBOARDING_SERVICE_ACCOUNT_ID).
        </div>
      )}

      {isLoading ? (
        <p className={s.status}>Loading mapping preview…</p>
      ) : redesignEnabled ? (
        <div className={s.clusters}>
          {visibleGroups.length === 0 ? (
            <p className={s.emptyCluster}>No rows match this filter.</p>
          ) : (
            visibleGroups.map((group) => {
              const isOpen = openClusters.has(group.clusterId);
              const chipClass =
                group.config.kind === "error"
                  ? s.countChip_error
                  : group.config.kind === "changed"
                    ? s.countChip_changed
                    : s.countChip_passed;
              return (
                <article
                  key={group.clusterId}
                  className={s.cluster}
                  data-testid={`pci-cluster-${group.clusterId}`}
                >
                  <button
                    type="button"
                    className={s.clusterHeader}
                    aria-expanded={isOpen}
                    aria-label={`${group.config.title}, ${group.rows.length} rows, ${isOpen ? "expanded" : "collapsed"}`}
                    onClick={() => toggleCluster(group.clusterId)}
                  >
                    <div className={s.clusterTitleGroup}>
                      <span className={s.clusterTitle}>{group.config.title}</span>
                      {group.config.hint ? (
                        <span className={s.clusterHint}>{group.config.hint}</span>
                      ) : null}
                    </div>
                    <span className={`${s.countChip} ${chipClass}`} data-testid="cluster-count">
                      {group.rows.length}
                    </span>
                  </button>
                  {isOpen && (
                    <div className={s.clusterBody}>
                      {renderRowTable(group.rows.map((item) => item.row))}
                    </div>
                  )}
                </article>
              );
            })
          )}
        </div>
      ) : (
        renderRowTable(pageItems)
      )}

      {!isLoading && showPager && (
        <Pagination
          page={page}
          pageCount={pageCount}
          total={total}
          pageSize={pageSize}
          onChange={setPage}
          onPageSizeChange={(n) => updateState({ pageSize: clampPageSize(n) })}
        />
      )}

      {confirmError && !redesignEnabled && (
        <div className={s.error} role="alert">
          <strong>Import failed:</strong> {confirmError}
        </div>
      )}

      {redesignEnabled ? (
        <WorkflowActionBar
          ariaLabel="Pay component import review actions"
          status={actionBarStatus}
          statusRole={confirmError ? "alert" : "status"}
          leadingActions={
            <>
              <Button
                variant="secondary"
                onPress={handleRequestCancelImport}
                isDisabled={reviewBusy}
              >
                Cancel import
              </Button>
              <Button
                variant="tertiary"
                onPress={() => navigate(backTarget)}
                isDisabled={reviewBusy}
              >
                Back
              </Button>
            </>
          }
          trailingActions={
            <ButtonGroup align="right">
              {hasErrors && (
                <Button
                  variant="secondary"
                  onPress={() =>
                    downloadCsvText(
                      buildFailedRowsCsv(state.csvText ?? "", importResult.errors),
                      "pay-component-import-errors.csv",
                    )
                  }
                  isDisabled={reviewBusy}
                >
                  Download errors CSV
                </Button>
              )}
              {showRevalidate ? (
                <Button
                  variant="primary"
                  onPress={() => void handleRevalidate()}
                  isDisabled={reviewBusy || state.mypayEnvironment === null}
                >
                  {isValidating || isRevalidating
                    ? "Re-validating…"
                    : "Re-validate"}
                </Button>
              ) : hasErrors ? (
                <Button
                  variant="primary"
                  isDisabled={
                    validCount === 0 ||
                    !commitDataReady ||
                    !commitReady ||
                    reviewBusy ||
                    validationStale
                  }
                  onPress={handleRequestPartialImport}
                >
                  {`Import ${validCount} valid row(s)`}
                </Button>
              ) : (
                <Button
                  variant="primary"
                  isDisabled={!canConfirm || reviewBusy}
                  onPress={handleRequestConfirm}
                >
                  {`Confirm import (${reviewRows.length})`}
                </Button>
              )}
            </ButtonGroup>
          }
        />
      ) : (
        <ButtonGroup align="right">
          <Button variant="tertiary" onPress={() => navigate(backTarget)}>
            Back to mapping
          </Button>
          {hasErrors ? (
            <>
              <Button
                variant="secondary"
                onPress={() =>
                  downloadCsvText(
                    buildFailedRowsCsv(state.csvText ?? "", importResult.errors),
                    "pay-component-import-errors.csv",
                  )
                }
              >
                Download errors CSV
              </Button>
              <Button
                variant="primary"
                isDisabled={
                  validCount === 0 ||
                  !commitDataReady ||
                  !commitReady ||
                  isCommitting
                }
                onPress={() => void handleImportValid()}
              >
                {`Import ${validCount} valid row(s)`}
              </Button>
            </>
          ) : (
            <Button
              variant="primary"
              isDisabled={!canConfirm}
              onPress={() => void handleConfirm()}
            >
              {`Confirm import (${reviewRows.length})`}
            </Button>
          )}
        </ButtonGroup>
      )}

      <CancelImportConfirmModal
        isOpen={cancelConfirmOpen}
        onKeepImporting={() => setCancelConfirmOpen(false)}
        onCancelAnyway={handleConfirmCancelImport}
      />

      <CommitImportConfirmModal
        isOpen={commitConfirmOpen}
        rowCount={reviewRows.length}
        companyName={companyName}
        companyCode={companyCode}
        environment={state.mypayEnvironment ?? "nz-datapay"}
        componentColumn={componentColumn}
        onCancel={() => setCommitConfirmOpen(false)}
        onConfirm={() => {
          setCommitConfirmOpen(false);
          void handleConfirm();
        }}
      />

      <PartialImportConfirmModal
        isOpen={partialConfirmOpen}
        validCount={validCount}
        skippedCount={errorsByRow.size}
        onCancel={() => setPartialConfirmOpen(false)}
        onConfirm={() => {
          setPartialConfirmOpen(false);
          void handleImportValid();
        }}
      />

      <PayComponentRowEditModal
        isOpen={editingRowIndex !== null}
        row={editingMappedRow}
        sourceRow={editingSourceRow}
        headers={preview.headers}
        componentColumn={componentColumn}
        startDateColumn={state.startDateColumn}
        endDateColumn={state.endDateColumn}
        rowErrors={editingRowIndex ? errorsByRow.get(editingRowIndex) ?? [] : []}
        isProcessing={isCommitting}
        onCancel={() => setEditingRowIndex(null)}
        onSave={handleRowSave}
      />

      {redesignEnabled && (
        <UnsavedProgressConfirmModal
          isOpen={isBlocked}
          title="Leave pay component import?"
          message="Uploaded rows, mappings, and validation progress will be lost."
          onStay={stay}
          onLeave={handleLeaveAnyway}
        />
      )}
    </div>
  );
}
